import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MessageSquare, LifeBuoy, Send, ChevronDown } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { RECIPIENT_EMAIL, CONTACT_EMAIL } from "../data";
import { notify } from "../notify";
import { useAsaAuth } from "../AuthContext";

const FAQS = [
  { q: "Is the symptom checker a diagnosis?", a: "No. It provides a general indication only, weighted by your symptoms and family history. Always consult a qualified healthcare professional for diagnosis." },
  { q: "How is my data used?", a: `Your entries are stored securely in your ASAL account so you can track your health and prepare for appointments. Fully anonymised and aggregated data may support autoimmune research — only ever with your consent.` },
  { q: "Can I cancel my membership?", a: "Yes, you can cancel anytime from the Account page. Your access continues until the end of your billing period." },
  { q: "Why are reports charged separately?", a: "Diagnostic tools and GP/Doctor report downloads carry a small per-download fee that helps fund the ASA Foundation's work." },
];

export default function Support() {
  const { user } = useAsaAuth();
  const [form, setForm] = useState({ subject: "", message: "" });
  const [open, setOpen] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const send = (e) => {
    e.preventDefault();
    if (!form.subject || !form.message) { toast.error("Please add a subject and message"); return; }
    const from = user?.email || "unknown";
    notify("support", `From: ${from} (${user?.name || "member"})\nSubject: ${form.subject}\n\n${form.message}`, user?.email, `ASAL support: ${form.subject}`);
    setForm({ subject: "", message: "" });
    toast.success("Message sent to the ASA Foundation team");
    toast.message("We'll reply to your registered email address");
  };

  return (
    <AppLayout title="ASAL Support">
      <div className="bg-gradient-to-r from-[#0d9488] to-[#0f766e] rounded-2xl p-6 text-white flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center"><LifeBuoy size={26} /></div>
        <div><h2 className="font-head font-bold text-xl">We're here to help</h2><p className="text-white/85 text-sm mt-0.5">Reach the ASA Foundation team — we usually reply within 1 working day.</p></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
        <div className="space-y-4">
          <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email" className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 p-4 hover:shadow-sm transition-shadow">
            <div className="w-10 h-10 rounded-lg bg-[#e6f4f2] text-[#0d9488] flex items-center justify-center"><Mail size={20} /></div>
            <div><p className="text-[12px] text-slate-400">Contact us</p><p className="font-semibold text-slate-700 text-sm break-all">{CONTACT_EMAIL}</p></div>
          </a>
          <a href={`mailto:${RECIPIENT_EMAIL}`} className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 p-4 hover:shadow-sm transition-shadow">
            <div className="w-10 h-10 rounded-lg bg-[#e6f4f2] text-[#0d9488] flex items-center justify-center"><Mail size={20} /></div>
            <div><p className="text-[12px] text-slate-400">ASA Foundation</p><p className="font-semibold text-slate-700 text-sm break-all">{RECIPIENT_EMAIL}</p></div>
          </a>
          <div className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 p-4">
            <div className="w-10 h-10 rounded-lg bg-[#e6f4f2] text-[#0d9488] flex items-center justify-center"><Phone size={20} /></div>
            <div><p className="text-[12px] text-slate-400">Helpline</p><p className="font-semibold text-slate-700 text-sm">+44 20 7946 0123</p></div>
          </div>
          <div className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 p-4">
            <div className="w-10 h-10 rounded-lg bg-[#e6f4f2] text-[#0d9488] flex items-center justify-center"><MessageSquare size={20} /></div>
            <div><p className="text-[12px] text-slate-400">Community</p><p className="font-semibold text-slate-700 text-sm">Peer support forum</p></div>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-head font-semibold text-slate-800">Send us a message</h3>
          <form onSubmit={send} className="space-y-4 mt-4">
            <input value={form.subject} onChange={set("subject")} placeholder="Subject" className="w-full h-11 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#0d9488]" />
            <textarea value={form.message} onChange={set("message")} rows={5} placeholder="How can we help?" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#0d9488] resize-none" />
            <button type="submit" className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] text-white font-semibold text-sm"><Send size={16} /> Send message</button>
          </form>
        </div>
      </div>

      <h3 className="font-head font-semibold text-slate-800 mt-8 mb-3">Frequently asked questions</h3>
      <div className="space-y-2">
        {FAQS.map((f, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between px-4 py-3.5 text-left">
              <span className="font-semibold text-slate-700 text-[15px]">{f.q}</span>
              <ChevronDown size={18} className={`text-slate-400 transition-transform ${open === i ? "rotate-180" : ""}`} />
            </button>
            {open === i && <p className="px-4 pb-4 text-[14px] text-slate-500 leading-relaxed">{f.a}</p>}
          </div>
        ))}
      </div>
    </AppLayout>
  );
}
